"use client"
import React, { useState } from 'react';
import { View, StyleSheet, Pressable, GestureResponderEvent, Image as RNImage } from 'react-native';
import { Text, IconButton, Menu, useTheme } from 'react-native-paper';
import { TreeItemProps } from './types';
import { getNodeIcon } from './utils';

export const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  onSelect,
  isSelected,
  selectedNodes,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const theme = useTheme();

  const hasChildren = node.children && node.children.length > 0

  const handleSelect = (e: GestureResponderEvent) => {
    // For web compatibility
    if (e.stopPropagation) {
      e.stopPropagation();
    }

    // Only select children for line and section nodes
    const selectChildren = node.type === 'line' || node.type === 'section';
    if (onSelect) {
      onSelect(node, selectChildren);
    }

    // Auto-expand when selecting a parent node
    if (selectChildren && node.children?.length && !expanded) {
      setExpanded(true);
    }
  };

  const handleContextMenu = (e: GestureResponderEvent) => {
    if (e.preventDefault) {
      e.preventDefault()
    }
    setMenuPosition({ x: e.nativeEvent.pageX, y: e.nativeEvent.pageY })
    setMenuVisible(true)
  }

  const handleExpand = (e: GestureResponderEvent) => {
    if (e.stopPropagation) {
      e.stopPropagation()
    }
    setExpanded(!expanded)
  }

  const closeMenu = () => {
    setMenuVisible(false)
  }

  const handleViewDrawing = () => {
    closeMenu();
    // TODO: Implement drawing view
    console.log('View drawing for:', node.name);
  };

  const handleExportDrawing = () => {
    closeMenu();
    // TODO: Implement drawing export
    console.log('Export drawing for:', node.name);
  };

  const handlePrintDrawing = () => {
    closeMenu();
    // TODO: Implement drawing print
    console.log('Print drawing for:', node.name);
  };

  const getIndentation = () => {
    if (node.type === "tower" || node.type === "span") {
      return (level - 1) * 6 + 12 // Reduced spacing
    }
    return level * 6 // Reduced spacing
  }

  // Get the appropriate icon based on node type and properties
  const iconSource = getNodeIcon(node.type, node.properties)

  return (
    <View>
      <Pressable
        onPress={handleSelect}
        onLongPress={handleContextMenu}  // Use onLongPress for mobile
        //@ts-ignore
        onContextMenu={handleContextMenu}  // Keep onContextMenu for web
        style={[
          styles.treeItem,
          { marginLeft: getIndentation() },
          isSelected && { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <View style={styles.treeItemContent}>
          {hasChildren ? (
            <IconButton
              icon={expanded ? "chevron-down" : "chevron-right"}
              size={24}
              style={styles.chevron}
              onPress={handleExpand}
              iconColor={theme.colors.onSurface}
            />
          ) : (
            <View style={styles.iconPlaceholder} />
          )}
          <View style={styles.iconWrapper}>
            <RNImage
              source={iconSource}
              style={[
                styles.icon,
                // Only apply tintColor if it's not a tension tower
                node.type === "tower" && node.properties?.IsTension === true
                  ? { opacity: 0.9 } // No tintColor for tension towers, slightly higher opacity
                  : {
                    tintColor: isSelected ? theme.colors.primary : theme.colors.onSurface,
                    opacity: 0.8,
                  },
              ]}
              resizeMode="contain"
            />
          </View>
          <Text
            variant="bodyMedium"
            style={[
              styles.treeItemText,
              { color: theme.colors.onSurface },
              isSelected && { color: theme.colors.primary },
            ]}
          >
            {node.name}
          </Text>
          <Menu
            visible={menuVisible}
            onDismiss={closeMenu}
            anchor={{ x: menuPosition.x, y: menuPosition.y }}
            contentStyle={{ backgroundColor: theme.colors.elevation.level2 }}
          >
            <Menu.Item
              leadingIcon="file-document-outline"
              onPress={handleViewDrawing}
              title="View Drawing"
              titleStyle={styles.menuItemText}
              style={styles.menuItem}
              dense
            />
            <Menu.Item
              leadingIcon="export"
              onPress={handleExportDrawing}
              title="Export Drawing"
              titleStyle={styles.menuItemText}
              style={styles.menuItem}
              dense
            />
            <Menu.Item
              leadingIcon="printer"
              onPress={handlePrintDrawing}
              title="Print Drawing"
              titleStyle={styles.menuItemText}
              style={styles.menuItem}
              dense
            />
          </Menu>
        </View>
      </Pressable>
      {expanded && hasChildren && (
        <View style={styles.childrenContainer}>
          {node.children?.map((child) => (
            <TreeItem
              key={child.name}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              isSelected={selectedNodes?.includes(child.name)}
              selectedNodes={selectedNodes}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  treeItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 1, // Reduced padding
    cursor: "pointer",
    borderRadius: 2,
  },
  treeItemContent: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minHeight: 10,
  },
  treeItemText: {
    fontSize: 13,
    flex: 1,
    lineHeight: 18,
  },
  iconWrapper: {
    width: 28,
    height: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  icon: {
    width: 24,
    height: 24,
  },
  chevron: {
    margin: 0,
    padding: 0,
    width: 24,
    height: 24,
  },
  iconPlaceholder: {
    width: 24,
    height: 24,
  },
  childrenContainer: {
    marginLeft: 2, // Significantly reduced margin
  },
  menuItem: {
    minHeight: 32,
    height: 32,
  },
  menuItemText: {
    fontSize: 14, // Increased from 13
  },
});
